// Shared timezone/date helpers used by Calendar and TimePicker components

import { SUPPORTED_TIMEZONE_IDS } from '../constants';

/**
 * Convert a source timestamp (or now) into a Date object representing that
 * instant in the provided IANA timezone by reconstructing local parts.
 */
export function getTargetTimezoneDate(
    timeZone: string,
    timestamp?: number,
): Date {
    const sourceDate = timestamp ? new Date(timestamp) : new Date();

    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });

    const formatted = formatter.format(sourceDate);
    const [datePart, timePart] = formatted.split(', ');
    if (!datePart || !timePart) {
        throw new Error(`Invalid date format: ${formatted}`);
    }

    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);

    if (
        year === undefined ||
        month === undefined ||
        day === undefined ||
        hour === undefined ||
        minute === undefined ||
        second === undefined
    ) {
        throw new Error(
            `Invalid date parts parsed from: ${datePart}, ${timePart}`,
        );
    }

    return new Date(year, month - 1, day, hour, minute, second);
}

/**
 * Returns whether the given date is eligible for booking in the target timezone.
 * Weekends are now considered available by default; callers can still control
 * availability via busy/disabled day inputs. Past dates remain unavailable.
 */
export function hasAvailableWorkingHours(
    timeZone: string,
    date: Date,
    now?: Date,
): boolean {
    const todayInTarget = getTargetTimezoneDate(timeZone, now?.getTime());
    const dateNormalized = new Date(date);
    const todayNormalized = new Date(todayInTarget);
    dateNormalized.setHours(0, 0, 0, 0);
    todayNormalized.setHours(0, 0, 0, 0);

    return dateNormalized >= todayNormalized;
}

/**
 * Find the next available day in the target timezone. If after 4pm local, start from next day.
 */
export function getNextAvailableWorkingDay(timeZone: string, now?: Date): Date {
    const today = getTargetTimezoneDate(timeZone, now?.getTime());
    const checkDate = new Date(today);

    const currentHour = today.getHours();
    const isAfterWorkingHours = currentHour >= 16;
    if (isAfterWorkingHours) {
        checkDate.setDate(checkDate.getDate() + 1);
    }

    // Avoid potential infinite loop: cap at 31 days ahead
    for (
        let i = 0;
        i < 31 && !hasAvailableWorkingHours(timeZone, checkDate, now);
        i++
    ) {
        checkDate.setDate(checkDate.getDate() + 1);
    }

    return checkDate;
}

/**
 * Compute the UTC timestamp that represents local midnight in the provided timezone, for the given local Date.
 */
export function getUtcMidnightTimestampForDateInTimezone(
    localMidnight: Date,
    timeZone: string,
): number {
    // Derive the calendar date parts from the provided Date (ignores system TZ)
    const year = localMidnight.getFullYear();
    const month = localMidnight.getMonth();
    const day = localMidnight.getDate();

    // Use a stable reference instant (UTC noon) to determine the offset on that calendar date
    const referenceInstant = new Date(Date.UTC(year, month, day, 12, 0, 0));
    const offsetFormatter = new Intl.DateTimeFormat('en', {
        timeZone,
        timeZoneName: 'longOffset',
    });
    const offsetParts = offsetFormatter.formatToParts(referenceInstant);
    const offsetPart = offsetParts.find((part) => part.type === 'timeZoneName');
    const offsetString = offsetPart?.value || '+00:00';

    // Parse offset like +13:00 or +10:30
    const match = offsetString.match(/([+-])(\d{1,2}):(\d{2})/);
    const sign = match?.[1] === '+' ? 1 : -1;
    const hours = match?.[2] ? parseInt(match[2], 10) : 0;
    const minutes = match?.[3] ? parseInt(match[3], 10) : 0;
    const offsetMinutes = sign * (hours * 60 + minutes);

    // Compute the UTC timestamp that corresponds to 00:00 local in the target timezone
    // local = UTC + offset  =>  UTC = local - offset
    const utcMs =
        Date.UTC(year, month, day, 0, 0, 0) - offsetMinutes * 60 * 1000;
    return utcMs;
}

/**
 * Convenience: next available working day's UTC midnight timestamp (consistent with Calendar initial value).
 */
export function getNextAvailableWorkingDayTimestamp(
    timeZone: string,
    now?: Date,
): number {
    const nextDate = getNextAvailableWorkingDay(timeZone, now);
    const localMidnight = new Date(
        nextDate.getFullYear(),
        nextDate.getMonth(),
        nextDate.getDate(),
        0,
        0,
        0,
        0,
    );
    return getUtcMidnightTimestampForDateInTimezone(localMidnight, timeZone);
}

/** Config for supported timezones: standard names and optional DST (offset indicates DST) */
const SUPPORTED_TZ_NAMES: Record<
    string,
    {
        standard: { short: string; full: string };
        dst?: { short: string; full: string; offset: number };
    }
> = {
    'Pacific/Auckland': {
        standard: { short: 'NZST', full: 'New Zealand Standard Time' },
        dst: { short: 'NZDT', full: 'New Zealand Daylight Time', offset: 13 },
    },
    'Australia/Sydney': {
        standard: { short: 'AEST', full: 'Australian Eastern Standard Time' },
        dst: {
            short: 'AEDT',
            full: 'Australian Eastern Daylight Time',
            offset: 11,
        },
    },
    'Australia/Brisbane': {
        standard: { short: 'AEST', full: 'Australian Eastern Standard Time' },
    },
    'Australia/Adelaide': {
        standard: { short: 'ACST', full: 'Australian Central Standard Time' },
        dst: {
            short: 'ACDT',
            full: 'Australian Central Daylight Time',
            offset: 10.5,
        },
    },
    'Australia/Darwin': {
        standard: { short: 'ACST', full: 'Australian Central Standard Time' },
    },
    'Australia/Perth': {
        standard: { short: 'AWST', full: 'Australian Western Standard Time' },
    },
    'America/New_York': {
        standard: { short: 'EST', full: 'Eastern Standard Time' },
        dst: { short: 'EDT', full: 'Eastern Daylight Time', offset: -4 },
    },
    'America/Chicago': {
        standard: { short: 'CST', full: 'Central Standard Time' },
        dst: { short: 'CDT', full: 'Central Daylight Time', offset: -5 },
    },
    'America/Denver': {
        standard: { short: 'MST', full: 'Mountain Standard Time' },
        dst: { short: 'MDT', full: 'Mountain Daylight Time', offset: -6 },
    },
    'America/Phoenix': {
        standard: { short: 'MST', full: 'Mountain Standard Time' },
    },
    'America/Los_Angeles': {
        standard: { short: 'PST', full: 'Pacific Standard Time' },
        dst: { short: 'PDT', full: 'Pacific Daylight Time', offset: -7 },
    },
};

/**
 * Determine current offset and human-readable timezone names for a given IANA zone and date.
 */
export function getCurrentTimezoneInfo(
    timeZoneId: string,
    date: Date = new Date(),
): { offset: number; shortName: string; fullName: string } {
    const formatter = new Intl.DateTimeFormat('en', {
        timeZone: timeZoneId,
        timeZoneName: 'longOffset',
    });

    const offsetPart = formatter
        .formatToParts(date)
        .find((part) => part.type === 'timeZoneName');
    const offsetString = offsetPart?.value || '+00:00';

    const offsetMatch = offsetString.match(/([+-])(\d{1,2}):?(\d{2})?/);
    let offset = 0;
    if (offsetMatch && offsetMatch.length > 2) {
        const sign = offsetMatch[1] === '+' ? 1 : -1;
        const hoursStr = offsetMatch[2];
        const minutesStr = offsetMatch[3] || '0';
        if (!hoursStr) {
            throw new Error(`Invalid hours from offset: ${offsetString}`);
        }
        const hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);
        offset = sign * (hours + minutes / 60);
    }

    const config = SUPPORTED_TZ_NAMES[timeZoneId];
    if (config) {
        const isDST = config.dst && offset === config.dst.offset;
        const names = isDST && config.dst ? config.dst : config.standard;
        return {
            offset,
            shortName: names.short,
            fullName: names.full,
        };
    }

    const shortFormatter = new Intl.DateTimeFormat('en', {
        timeZone: timeZoneId,
        timeZoneName: 'short',
    });
    const longFormatter = new Intl.DateTimeFormat('en', {
        timeZone: timeZoneId,
        timeZoneName: 'long',
    });
    const shortPart = shortFormatter
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName');
    const longPart = longFormatter
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName');
    const shortValue = shortPart?.value ?? 'UTC';
    const fullValue = longPart?.value ?? 'Coordinated Universal Time';
    // When Intl returns GMT offset, derive short name from full name (e.g. "China Standard Time" -> "CST")
    const shortName = /^GMT[+-]\d/.test(shortValue)
        ? fullValue
              .split(/\s+/)
              .map((w) => w[0])
              .join('')
              .toUpperCase()
        : shortValue;
    return {
        offset,
        shortName,
        fullName: fullValue,
    };
}

/**
 * Detect the user's timezone from browser and map to supported timezones.
 * Returns an IANA timezone identifier that matches one of our supported timezones.
 * Note: Brisbane is handled separately as it doesn't observe DST.
 */
export function detectUserTimezone(): string {
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (SUPPORTED_TIMEZONE_IDS.includes(userTz)) return userTz;

    if (userTz.includes('Auckland') || userTz.includes('Wellington')) {
        return 'Pacific/Auckland';
    }
    if (userTz.includes('Brisbane')) {
        return 'Australia/Brisbane';
    }
    if (userTz.includes('Sydney') || userTz.includes('Melbourne')) {
        return 'Australia/Sydney';
    }
    if (userTz.includes('Adelaide')) {
        return 'Australia/Adelaide';
    }
    if (userTz.includes('Darwin')) {
        return 'Australia/Darwin';
    }
    if (userTz.includes('Perth')) {
        return 'Australia/Perth';
    }
    if (userTz.includes('New_York') || userTz.includes('Toronto')) {
        return 'America/New_York';
    }
    if (userTz.includes('Chicago') || userTz.includes('Mexico_City')) {
        return 'America/Chicago';
    }
    // Phoenix (Arizona) does not observe DST; Denver does. Map separately to avoid 1-hour offset during DST.
    if (userTz.includes('Phoenix')) {
        return 'America/Phoenix';
    }
    if (userTz.includes('Denver')) {
        return 'America/Denver';
    }
    if (userTz.includes('Los_Angeles') || userTz.includes('Vancouver')) {
        return 'America/Los_Angeles';
    }

    return 'Pacific/Auckland';
}

/**
 * Format a timezone ID for display, replacing underscores with spaces and adding the short name.
 *
 * @param timeZoneId - IANA timezone identifier (e.g., "America/New_York")
 * @param date - The date to use for calculating the offset (for DST handling)
 * @returns Formatted string like "New York (EDT)" or "Shanghai (CST)"
 */
export function formatTimezoneDisplay(
    timeZoneId: string,
    date: Date = new Date(),
): string {
    const tzInfo = getCurrentTimezoneInfo(timeZoneId, date);
    const cityName =
        timeZoneId.split('/').pop()?.replace(/_/g, ' ') || timeZoneId;
    return `${cityName} (${tzInfo.shortName})`;
}
